import {
    AppShell,
    useMantineColorScheme,
    useMantineTheme,
} from '@mantine/core'
import {createContext, useEffect, useState} from 'react'
import {Outlet, useLocation, useSearchParams, SetURLSearchParams} from 'react-router-dom'
import Sidenav from './sidenav'
import {logout} from '../utils'
import {useMediaQuery} from '@mantine/hooks'
import useIsAuthenticated from "react-auth-kit/hooks/useIsAuthenticated";
import AppHeader from "./AppHeader";

/**
 * Context value for stats navigation, providing access to URL search params.
 * @property searchParams - The current URL search parameters.
 * @property setSearchParams - Function to update the URL search parameters.
 */
interface StatsNavContextValue {
    searchParams: URLSearchParams;
    setSearchParams: SetURLSearchParams;
}

export const StatsNavContext = createContext<StatsNavContextValue | undefined>(undefined)

export default function Layout() {
    const isAuthenticated = useIsAuthenticated();
    const location = useLocation()

    // Mobile sidebar state - controls whether sidebar is visible on mobile
    const [mobileOpened, setMobileOpened] = useState(false)

    // Use standard Tailwind sm breakpoint (640px) for consistency
    const isAboveMobile = useMediaQuery('(min-width: 640px)')
    const [searchParams, setSearchParams] = useSearchParams({})

    const { colorScheme, toggleColorScheme } = useMantineColorScheme();
    const theme = useMantineTheme()

    // Sync Tailwind dark mode class with Mantine color scheme
    useEffect(() => {
        if(colorScheme === 'dark'){
            document.documentElement.classList.add('dark');
            document.documentElement.classList.remove('light');
        }else{
            document.documentElement.classList.remove('dark');
            document.documentElement.classList.add('light');
        }
    }, [colorScheme]);

    // Update document title based on route
    useEffect(() => {
        const title = location.pathname
            .split('/')
            .filter((s) => s)
            .map((s) => s[0].toUpperCase() + s.slice(1))
            .map((s) => s.replace('%20', ' '))
            .join(' - ')
        document.title = title ? `${title} - CSC` : 'CSConfederation'
    }, [location])

    // Close mobile sidebar when navigating to a new page
    useEffect(() => {
        setMobileOpened(false);
    }, [location.pathname]);

    const handleLogout = async () => {
        await logout()
    }

    return (
        <StatsNavContext.Provider value={{searchParams, setSearchParams}}>
            <AppShell
                header={{ height: 48 }}
                navbar={{
                    width: 200,
                    breakpoint: 'sm',
                    collapsed: { mobile: !mobileOpened }
                }}
                padding={0}
            >
                <AppShell.Header>
                    <AppHeader
                        sidenavOpened={mobileOpened}
                        setSidenavOpened={setMobileOpened}
                        theme={theme}
                        colorScheme={colorScheme}
                        toggleColorScheme={toggleColorScheme}
                        isAuthenticated={isAuthenticated}
                        smallScreen={isAboveMobile ?? false}
                    />
                </AppShell.Header>

                <AppShell.Navbar p="xs">
                    <Sidenav
                        opened={mobileOpened}
                        setSidenavOpened={setMobileOpened}
                        handleLogout={handleLogout}
                    />
                </AppShell.Navbar>

                <AppShell.Main
                    className="min-h-0"
                    style={{ background: 'var(--mantine-color-body)' }}
                >
                    <div className="h-full min-h-0">
                        <Outlet/>
                    </div>
                </AppShell.Main>
            </AppShell>
        </StatsNavContext.Provider>
    )
}
